import {
  AlumniEarningsInfo,
  CONFIG,
  CostLineItem,
  CostOfAttendanceSummary,
  EducationLevelEnum,
  GraduationRateInfo,
  GrantsAndScholarshipsSummary,
  IncomeRangeEnum,
  Institution,
  LivingConditionTypeEnum,
  LoanLimits,
  MedianYearsOfStudyInfo,
  PercentileCareerDistribution,
  ResidencyTypeEnum,
  StartingYearDelayInfo
} from '@lib/utils';

import { RocCalculatorInput } from '../../models';
import { CostOfAttendanceStandardBreakdown, LivingArrangement, SalaryAnalysis } from '../../models/graphql';
import { RocModel } from '../roc-model';
import { UserModel } from '../user-model';

export class RocModelService {
  public static getStartingYearDelay(rocModel: RocModel, userModel: UserModel): StartingYearDelayInfo {
    const currentYear = new Date().getFullYear();
    const yearsUntilHighSchoolComplete = Math.abs(Math.min(userModel.educationLevel?.value ?? 0, 0));
    const educationCostStartYear = rocModel.startYear ?? currentYear;
    const yearsUntilInstitutionStart = Math.max(educationCostStartYear - currentYear, 0);

    const total =
      yearsUntilHighSchoolComplete > yearsUntilInstitutionStart
        ? yearsUntilHighSchoolComplete
        : yearsUntilInstitutionStart;
    return {
      total,
      yearsUntilHighSchoolComplete,
      yearsAfterHighSchoolBeforeInstitutionStart: Math.max(yearsUntilInstitutionStart - yearsUntilHighSchoolComplete, 0)
    };
  }

  public static getPellGrantAidByYear(rocModel: RocModel, userModel: UserModel): number[] {
    const efc = userModel.expectedFamilyContribution ?? 0;
    const costOfAttendanceByYear = this.getCostOfAttendanceByYear(rocModel, userModel);
    const yearsToCompleteDegree = rocModel.yearsToCompleteDegree!;
    const startingYearDelay = this.getStartingYearDelay(rocModel, userModel);

    if (!costOfAttendanceByYear) {
      return [];
    }

    const pellGrantAidByYear = [];

    for (let i = 0; i < yearsToCompleteDegree; i++) {
      let adjustedPellGrantAidForYear;

      if (rocModel.fullTimeStudentPercent === 1 && efc > 5711) {
        adjustedPellGrantAidForYear = 0;
      } else if (rocModel.fullTimeStudentPercent === 0.5 && efc > 5100) {
        adjustedPellGrantAidForYear = 0;
      } else {
        // adjust Efc using cola and adjust pellGrant using 2% adjustment
        const costOfLivingAdjustmentForYear = Math.pow(
          1 + CONFIG.COST_OF_LIVING_ADJUSTMENT,
          i + startingYearDelay.total
        );

        const adjustedEfc = efc * costOfLivingAdjustmentForYear;
        const pellGrantAdjustmentForYear = Math.pow(1 + CONFIG.PELL_AWARD_ADJUSTMENT, i + startingYearDelay.total);
        const pellGrantMaxLimitForYear = 6345 * pellGrantAdjustmentForYear;
        const pellGrantMinLimitForYear = 638 * pellGrantAdjustmentForYear;

        adjustedPellGrantAidForYear = Math.round(
          this.getPellGrantAidForYear(
            adjustedEfc,
            costOfAttendanceByYear.totalByYear[i],
            rocModel.fullTimeStudentPercent!,
            pellGrantMaxLimitForYear,
            pellGrantMinLimitForYear
          )
        );
      }

      pellGrantAidByYear.push(adjustedPellGrantAidForYear);
    }
    return pellGrantAidByYear;
  }

  public static getLoanLimitsInfo(rocModel: RocModel, userModel: UserModel): LoanLimits {
    const isTaxDependent: boolean = rocModel.isTaxDependent!;
    const yearsToCompleteDegree: number = rocModel.yearsToCompleteDegree!;
    const startingYearDelayInfo = this.getStartingYearDelay(rocModel, userModel);
    const costOfAttendanceSummary = this.getCostOfAttendanceByYear(rocModel, userModel);
    const efc = userModel.expectedFamilyContribution;
    const totalFederalLoanLimitByYear = [];
    const federalSubsidizedLoanLimitByYear = [];
    const isEligibleForSubsidizedLoanByYear = [];

    const taxDependencyLoanConstants = isTaxDependent
      ? CONFIG.LOAN_CONSTANTS.DEPENDENT
      : CONFIG.LOAN_CONSTANTS.INDEPENDENT;
    const federalSubsidizedLoanLimitAggregate = taxDependencyLoanConstants.FEDERAL_SUBSIDIZED.TOTAL;
    const totalFederalLoanLimitAggregate = taxDependencyLoanConstants.TOTAL_FEDERAL_LOAN.TOTAL;

    for (let i = 0; i < yearsToCompleteDegree; i++) {
      let adjustedEfc = efc;
      if (efc) {
        adjustedEfc = efc * Math.pow(1 + CONFIG.COST_OF_LIVING_ADJUSTMENT, i + startingYearDelayInfo.total);
      }

      const loanLimitsForYear = this.getLoanLimitsForYear(
        isTaxDependent,
        i + 1,
        costOfAttendanceSummary!.totalByYear[i],
        adjustedEfc
      );
      federalSubsidizedLoanLimitByYear.push(loanLimitsForYear.subsidizedLoanLimitForYear);
      totalFederalLoanLimitByYear.push(loanLimitsForYear.totalFederalLoanLimitForYear);
      isEligibleForSubsidizedLoanByYear.push(loanLimitsForYear.isEligibleForSubsidizedLoanForYear);
    }

    const loanLimitsModel: LoanLimits = {
      federalSubsidizedLoanLimitByYear,
      totalFederalLoanLimitByYear,
      federalSubsidizedLoanLimitAggregate,
      totalFederalLoanLimitAggregate,
      isEligibleForSubsidizedLoanByYear
    };
    return loanLimitsModel;
  }

  public static getOutOfPocketExpensesByYear(rocModel: RocModel, userModel: UserModel): number[] {
    const yearsToCompleteDegree = rocModel.yearsToCompleteDegree!;
    const institutionModel = rocModel.institutionModel!;

    if (yearsToCompleteDegree > 0 && institutionModel) {
      if (
        rocModel?.outOfPocketExpensesByYear?.length &&
        !(rocModel.outOfPocketExpensesByYear.length === 1 && rocModel.outOfPocketExpensesByYear[0] === 0)
      ) {
        return rocModel.outOfPocketExpensesByYear;
      } else {
        return this.getNetPriceByYear(rocModel, userModel).map(price => Math.max(0, price));
      }
    }

    return [0];
  }

  public static getNetPriceByYear(rocModel: RocModel, userModel: UserModel): number[] {
    const netPriceByYear: number[] = [];
    const costOfAttendanceSummary = this.getCostOfAttendanceByYear(rocModel, userModel);
    const grantsAndScholarshipsSummary = this.getGrantsAndScholarshipsByYear(rocModel, userModel);

    if (!costOfAttendanceSummary || !grantsAndScholarshipsSummary) {
      return [];
    }

    for (let i = 0; i < costOfAttendanceSummary.totalByYear.length; i++) {
      const netPrice = costOfAttendanceSummary.totalByYear[i] - grantsAndScholarshipsSummary.totalByYear[i];
      netPriceByYear.push(netPrice == null ? 0 : netPrice);
    }

    return netPriceByYear;
  }

  public static getMedianYearsOfStudy(rocModel: RocModel): MedianYearsOfStudyInfo {
    const institution = rocModel.institutionModel!;
    const isFullTime = rocModel.isFulltime!;
    const degreeLevel = rocModel.degreeLevel!;

    return this.getMedianYearsOfStudyInfo(institution, isFullTime, degreeLevel);
  }

  public static mapDatastopOccupationFallbackToPercentileSalaryFallback(
    salaryAnalysis: SalaryAnalysis
  ): PercentileCareerDistribution {
    let salaryFallback = undefined;

    if (
      salaryAnalysis?.minimumAnnualSalary &&
      salaryAnalysis?.medianAnnualSalary &&
      salaryAnalysis?.maximumAnnualSalary
    ) {
      salaryFallback = {
        percentile25: salaryAnalysis.minimumAnnualSalary.toString(),
        percentile50: salaryAnalysis.medianAnnualSalary.toString(),
        percentile75: salaryAnalysis.maximumAnnualSalary.toString(),
        type: '3percentiles'
      };
    } else if (salaryAnalysis?.minimumAnnualSalary && salaryAnalysis?.medianAnnualSalary) {
      salaryFallback = {
        percentile25: salaryAnalysis.minimumAnnualSalary.toString(),
        percentile50: salaryAnalysis.medianAnnualSalary.toString(),
        percentile75: salaryAnalysis.medianAnnualSalary.toString(),
        type: '3percentiles'
      };
    } else if (salaryAnalysis?.minimumAnnualSalary && salaryAnalysis?.maximumAnnualSalary) {
      salaryFallback = {
        percentile25: salaryAnalysis.minimumAnnualSalary.toString(),
        percentile50: salaryAnalysis.maximumAnnualSalary.toString(),
        percentile75: salaryAnalysis.maximumAnnualSalary.toString(),
        type: '3percentiles'
      };
    } else if (salaryAnalysis?.medianAnnualSalary && salaryAnalysis?.maximumAnnualSalary) {
      salaryFallback = {
        percentile25: salaryAnalysis.medianAnnualSalary.toString(),
        percentile50: salaryAnalysis.medianAnnualSalary.toString(),
        percentile75: salaryAnalysis.maximumAnnualSalary.toString(),
        type: '3percentiles'
      };
    }

    return salaryFallback!;
  }

  public static mapDatastopLocationFallbackToPercentileSalaryFallback(
    medianAnnualIndividualSalary: number
  ): PercentileCareerDistribution {
    const salaryFallback = {
      percentile25: medianAnnualIndividualSalary.toString(),
      percentile50: medianAnnualIndividualSalary.toString(),
      percentile75: medianAnnualIndividualSalary.toString(),
      type: '3percentiles'
    };

    return salaryFallback;
  }

  public static getTargetSalaryAnalysis(
    medianAnnualIndividualSalary: number | null | undefined,
    salaryAnalysis: SalaryAnalysis | null | undefined
  ): PercentileCareerDistribution {
    const locationSalaryAnalysis = medianAnnualIndividualSalary
      ? this.mapDatastopLocationFallbackToPercentileSalaryFallback(medianAnnualIndividualSalary)
      : {
          percentile25: '0',
          percentile50: '0',
          percentile75: '0',
          type: '3percentiles'
        };
    const occupationSalaryAnalysis = salaryAnalysis
      ? this.mapDatastopOccupationFallbackToPercentileSalaryFallback(salaryAnalysis)
      : undefined;
    const targetSalaryAnalysis = occupationSalaryAnalysis ?? locationSalaryAnalysis ?? null;

    return targetSalaryAnalysis;
  }

  public static toCalculatorInputForCurrentState(rocModel: RocModel, userModel: UserModel): RocCalculatorInput {
    const startDegreeLevel: number = userModel.educationLevel?.emsiDegreeLevel ?? 0;

    const currentRocCalculatorInput: RocCalculatorInput = {
      currentSalaryAnalysis: userModel.currentSalaryAnalysis
        ? userModel.currentSalaryAnalysis
        : this.mapDatastopOccupationFallbackToPercentileSalaryFallback(userModel.occupationModel!.salaryAnalysis!),
      goalSalaryAnalysis: userModel.currentSalaryAnalysis
        ? userModel.currentSalaryAnalysis
        : this.mapDatastopOccupationFallbackToPercentileSalaryFallback(userModel.occupationModel!.salaryAnalysis!),
      startDegreeLevel: startDegreeLevel,
      endDegreeLevel: startDegreeLevel,
      yearsOfCollege: 0,
      yearsToRetirement: Math.max(rocModel.retirementAge! - userModel.currentAge, 1),
      independent: false,
      ibrFederal: false,
      monthsToPayoffFederalLoan: 0,
      monthsToPayoffPrivateLoan: 0,
      costOfAttendanceByYear: [0],
      outOfPocketExpensesByYear: [0],
      grantOrScholarshipAidByYear: [0],
      federalSubsidizedLoanAmountByYear: [0],
      federalUnsubsidizedLoanAmountByYear: [0],
      privateLoanAmountByYear: [0],
      federalLoanInterest: CONFIG.FEDERAL_LOAN_INTEREST_RATE,
      privateLoanInterest: CONFIG.PRIVATE_LOAN_INTEREST_RATE,
      workDuringStudy: false,
      startingYearDelay: 0,
      noLoans: true,
      workTimeBeforeStudy: 0,
      alumniSalaryInfo: {
        after1Year: null,
        after2Years: null
      } as unknown as AlumniEarningsInfo
    };

    return currentRocCalculatorInput;
  }

  public static toCalculatorInputForGoalState(rocModel: RocModel, userModel: UserModel): RocCalculatorInput {
    const startDegreeLevel: number = userModel.educationLevel?.emsiDegreeLevel ?? 0;
    const endDegreeLevel: number = rocModel.degreeLevel?.emsiDegreeLevel ?? 0;
    const costOfAttendanceSummary = this.getCostOfAttendanceByYear(rocModel, userModel);
    const grantsAndScholarshipsSummary = this.getGrantsAndScholarshipsByYear(rocModel, userModel);
    const outOfPocketExpensesByYear = this.getOutOfPocketExpensesByYear(rocModel, userModel);
    const startingYearDelay: StartingYearDelayInfo = this.getStartingYearDelay(rocModel, userModel);
    const currentAndGoalLocIsSame: boolean =
      rocModel.locationModel?.autoCompleteModel?.id === userModel.locationModel?.autoCompleteModel?.id;

    const currentAndGoalCareerIsSame: boolean =
      rocModel.occupationModel?.onetCode === userModel.occupationModel?.onetCode;

    const currentAndGoalDegreeIsSame: boolean =
      rocModel.degreeLevel?.emsiDegreeLevel === userModel.educationLevel?.emsiDegreeLevel;

    const { alumniEarningsAfter1Year, alumniEarningsAfter2Years } =
      RocModelService.getCsEducationLevelAlumniEarnings(rocModel);

    const goalRocCalculatorInput: RocCalculatorInput = {
      currentSalaryAnalysis: userModel.currentSalaryAnalysis
        ? userModel.currentSalaryAnalysis
        : this.mapDatastopOccupationFallbackToPercentileSalaryFallback(userModel.occupationModel!.salaryAnalysis!),
      goalSalaryAnalysis: rocModel.goalSalaryAnalysis
        ? rocModel.goalSalaryAnalysis
        : currentAndGoalLocIsSame && currentAndGoalCareerIsSame && currentAndGoalDegreeIsSame
          ? userModel.currentSalaryAnalysis
            ? userModel.currentSalaryAnalysis
            : this.mapDatastopOccupationFallbackToPercentileSalaryFallback(userModel.occupationModel!.salaryAnalysis!)
          : this.mapDatastopOccupationFallbackToPercentileSalaryFallback(rocModel.occupationModel!.salaryAnalysis!),
      startDegreeLevel: startDegreeLevel,
      endDegreeLevel: endDegreeLevel,
      yearsOfCollege: rocModel.yearsToCompleteDegree ?? 0,
      yearsToRetirement: Math.max(rocModel.retirementAge! - userModel.currentAge, 1),
      independent: !rocModel.isTaxDependent,
      ibrFederal: rocModel.prefersIncomeBasedRepayment!,
      monthsToPayoffFederalLoan: rocModel.yearsToPayOffFederalLoan! * 12,
      monthsToPayoffPrivateLoan: rocModel.yearsToPayOffPrivateLoan! * 12,
      costOfAttendanceByYear: costOfAttendanceSummary?.totalByYear ?? [0],
      outOfPocketExpensesByYear: outOfPocketExpensesByYear,
      grantOrScholarshipAidByYear: grantsAndScholarshipsSummary?.totalByYear ?? [0],
      federalSubsidizedLoanAmountByYear: rocModel.federalSubsidizedLoanAmountByYear!,
      federalUnsubsidizedLoanAmountByYear: rocModel.federalUnsubsidizedLoanAmountByYear!,
      privateLoanAmountByYear: rocModel.privateLoanAmountByYear!,
      federalLoanInterest: CONFIG.FEDERAL_LOAN_INTEREST_RATE,
      privateLoanInterest: CONFIG.PRIVATE_LOAN_INTEREST_RATE,
      workDuringStudy: false,
      startingYearDelay: startingYearDelay.yearsUntilHighSchoolComplete,
      noLoans: false,
      workTimeBeforeStudy: startingYearDelay.yearsAfterHighSchoolBeforeInstitutionStart,
      alumniSalaryInfo: {
        after1Year: alumniEarningsAfter1Year,
        after2Years: alumniEarningsAfter2Years
      }
    };

    return goalRocCalculatorInput;
  }

  public static getCsEducationLevelAlumniEarnings(rocModel: RocModel): {
    alumniEarningsAfter1Year?: number;
    alumniEarningsAfter2Years?: number;
  } {
    const goalCipCode = rocModel.degreeProgram?.cipCode;
    const institutionProgramsForCipCode = rocModel.institutionModel?.institutionPrograms?.nodes?.filter(
      x => x.cipCode == goalCipCode
    );
    const goalDegreeCsEducationLevels = rocModel.degreeLevel?.csEducationLevels;

    let salaries: {
      alumniEarningsAfter1Year?: number;
      alumniEarningsAfter2Years?: number;
    } = {
      alumniEarningsAfter1Year: undefined,
      alumniEarningsAfter2Years: undefined
    };

    if (institutionProgramsForCipCode?.length && goalCipCode != null && goalDegreeCsEducationLevels?.length) {
      goalDegreeCsEducationLevels.forEach(csEducationLevelId => {
        institutionProgramsForCipCode.forEach(institutionProgramForCipCode => {
          const alumniEarnings = institutionProgramForCipCode.csEducationLevelAlumniEarningsList?.find(
            x => x.type === csEducationLevelId
          );
          if (alumniEarnings != null) {
            if (salaries == null) {
              salaries = alumniEarnings;
            } else {
              const previousTotal =
                (salaries?.alumniEarningsAfter1Year ?? 0) + (salaries?.alumniEarningsAfter2Years ?? 0);
              const currentTotal =
                (alumniEarnings?.alumniEarningsAfter1Year ?? 0) + (alumniEarnings?.alumniEarningsAfter2Years ?? 0);
              if (currentTotal > previousTotal) {
                salaries = alumniEarnings;
              }
            }
          }
        });
      });
    }

    return salaries;
  }

  public static getGrantsAndScholarshipsByYear(
    rocModel: RocModel,
    userModel: UserModel
  ): GrantsAndScholarshipsSummary | null {
    let grantsAndScholarshipsSummary!: GrantsAndScholarshipsSummary;

    if (rocModel.refineEducationCost?.grantsAndScholarshipsSummary != null) {
      grantsAndScholarshipsSummary = rocModel.refineEducationCost.grantsAndScholarshipsSummary;

      if (grantsAndScholarshipsSummary.federalPellGrant == null && rocModel.institutionModel) {
        grantsAndScholarshipsSummary = this.recalculatePellGrantAndTotalValueOnRefineEducationCost(rocModel, userModel);
      }
    } else {
      if (!rocModel.institutionModel) {
        return null;
      } else if (!rocModel.isFulltime) {
        grantsAndScholarshipsSummary = this.getGrantsAndScholarshipsByYearPartTime(rocModel, userModel);
      } else if (userModel.expectedFamilyContribution !== null) {
        grantsAndScholarshipsSummary = this.getGrantsAndScholarshipsByYearStrategy1(rocModel, userModel);
      } else if (userModel.incomeRange !== null && userModel.incomeRange.key !== IncomeRangeEnum.UNKNOWN.key) {
        grantsAndScholarshipsSummary = this.getGrantsAndScholarshipsByYearStrategy2(rocModel, userModel);
      } else {
        grantsAndScholarshipsSummary = this.getGrantsAndScholarshipsByYearStrategy3(rocModel, userModel);
      }
    }

    return grantsAndScholarshipsSummary;
  }

  public static getCostOfAttendanceByYear(
    rocModel: RocModel,
    userModel: UserModel,
    growthPredictionStrategy: 'colaRate' | 'rateChangeFromLastYear' = 'colaRate'
  ): CostOfAttendanceSummary | null {
    const yearsToCompleteDegree: number = rocModel.yearsToCompleteDegree ?? 0;
    let costOfAttendanceSummary: CostOfAttendanceSummary = {
      tuitionAndFees: [],
      booksAndSupplies: [],
      roomAndBoard: [],
      otherLivingExpenses: [],
      totalByYear: []
    };

    if (rocModel.refineEducationCost?.costOfAttendanceSummary) {
      costOfAttendanceSummary = rocModel.refineEducationCost?.costOfAttendanceSummary;
    } else {
      if (!rocModel.institutionModel) {
        return null;
      }

      const startingYearDelay = this.getStartingYearDelay(rocModel, userModel);

      const tuitionAndFees = this.calculateTuitionAndFees(rocModel);
      const booksAndSupplies = this.calculateBooksAndSupplies(rocModel);
      const roomAndBoard = this.calculateRoomAndBoard(rocModel);
      const otherLivingExpenses = this.calculateOtherLivingExpenses(rocModel);

      if (growthPredictionStrategy == 'rateChangeFromLastYear') {
        for (let yearIndex = 0; yearIndex < yearsToCompleteDegree; yearIndex++) {
          const tuitionAndFeesForYear =
            tuitionAndFees.expenseAmount !== null
              ? Math.round(
                  tuitionAndFees.expenseAmount *
                    Math.pow(1.0 + tuitionAndFees.percentChangeFromLastYear / 100, yearIndex + startingYearDelay.total)
                )
              : 0;
          const booksAndSuppliesForYear = Math.round(booksAndSupplies.expenseAmount ?? 0);
          const roomAndBoardForYear =
            roomAndBoard.expenseAmount !== null
              ? Math.round(
                  roomAndBoard.expenseAmount *
                    Math.pow(1.0 + roomAndBoard.percentChangeFromLastYear / 100, yearIndex + startingYearDelay.total)
                )
              : 0;
          const otherLivingExpensesForYear = Math.round(otherLivingExpenses.expenseAmount ?? 0);

          const totalForYear =
            tuitionAndFeesForYear + booksAndSuppliesForYear + roomAndBoardForYear + otherLivingExpensesForYear;

          costOfAttendanceSummary.tuitionAndFees.push(tuitionAndFeesForYear);
          costOfAttendanceSummary.booksAndSupplies.push(booksAndSuppliesForYear);
          costOfAttendanceSummary.roomAndBoard.push(roomAndBoardForYear);
          costOfAttendanceSummary.otherLivingExpenses.push(otherLivingExpensesForYear);
          costOfAttendanceSummary.totalByYear.push(totalForYear);
        }
      } else if (growthPredictionStrategy == 'colaRate') {
        for (let yearIndex = 0; yearIndex < yearsToCompleteDegree; yearIndex++) {
          const costOfLivingAdjustmentForYear = Math.pow(
            1.0 + CONFIG.COST_OF_LIVING_ADJUSTMENT,
            yearIndex + startingYearDelay.total
          );
          const tuitionAndFeesForYear =
            tuitionAndFees.expenseAmount !== null
              ? Math.round(tuitionAndFees.expenseAmount * costOfLivingAdjustmentForYear)
              : 0;
          const booksAndSuppliesForYear =
            booksAndSupplies.expenseAmount !== null
              ? Math.round(booksAndSupplies.expenseAmount * costOfLivingAdjustmentForYear)
              : 0;
          const roomAndBoardForYear =
            roomAndBoard.expenseAmount !== null
              ? Math.round(roomAndBoard.expenseAmount * costOfLivingAdjustmentForYear)
              : 0;
          const otherLivingExpensesForYear =
            otherLivingExpenses.expenseAmount !== null
              ? Math.round(otherLivingExpenses.expenseAmount * costOfLivingAdjustmentForYear)
              : 0;
          const totalForYear =
            tuitionAndFeesForYear + booksAndSuppliesForYear + roomAndBoardForYear + otherLivingExpensesForYear;

          costOfAttendanceSummary.tuitionAndFees.push(tuitionAndFeesForYear);
          costOfAttendanceSummary.booksAndSupplies.push(booksAndSuppliesForYear);
          costOfAttendanceSummary.roomAndBoard.push(roomAndBoardForYear);
          costOfAttendanceSummary.otherLivingExpenses.push(otherLivingExpensesForYear);
          costOfAttendanceSummary.totalByYear.push(totalForYear);
        }
      }
    }

    return costOfAttendanceSummary;
  }

  public static getMedianYearsOfStudyInfo(
    institution: Institution,
    isFullTime: boolean,
    degreeLevel: EducationLevelEnum
  ): MedianYearsOfStudyInfo {
    if (!institution) {
      return { years: null, graduationRate: null };
    }

    const { graduationProbability, graduationTimeFactor } = this.getGraduationRateInfo(institution, isFullTime);

    const n: number = graduationProbability.length;
    const normalPeriodOfStudy = degreeLevel?.normalYearsOfStudy ?? 4;

    let found = false;

    let j!: number;
    for (let i = 0; i < n && !found; i++) {
      if (graduationProbability[i] >= 0.5) {
        found = true;
        j = i;
      }
    }

    if (!found) {
      j = n;
    }
    let time: number;
    let rate: number;

    if (j === 0) {
      time = graduationTimeFactor[0];
      rate = graduationProbability[0];
    } else if (j === n) {
      time = graduationTimeFactor[n - 1];
      rate = graduationProbability[n - 1];
    } else {
      rate = 0.5;

      const x1: number = graduationTimeFactor[j - 1];
      const x2: number = graduationTimeFactor[j];

      const y1: number = graduationProbability[j - 1];
      const y2: number = graduationProbability[j];

      const k: number = (y2 - y1) / (x2 - x1);

      const dx: number = (rate - y1) / k;

      time = x1 + dx;
    }

    return {
      years: Math.ceil(time * normalPeriodOfStudy),
      graduationRate: rate
    };
  }

  public static getGraduationRateInfo(institution: Institution, isFullTime: boolean): GraduationRateInfo {
    const graduationProbability: number[] = [];
    const graduationTimeFactor = [1.0, 1.5, 2.0];

    let gr100!: number;
    let gr150!: number;
    let gr200!: number;

    if (institution) {
      if (isFullTime) {
        gr100 = institution?.fullTimeGraduationRates?.gr100 ?? 0;
        gr150 = institution?.fullTimeGraduationRates?.gr150Default ?? 0;
        gr200 = institution?.fullTimeGraduationRates?.gr200 ?? 0;
      } else {
        gr100 = institution?.partTimeGraduationRates?.gr100 ?? 0;
        gr150 = institution?.partTimeGraduationRates?.gr150 ?? 0;
        gr200 = institution?.partTimeGraduationRates?.gr200 ?? 0;
      }
    }

    graduationProbability.push(gr100);

    if (graduationProbability[graduationProbability.length - 1] > gr150) {
      gr150 = graduationProbability[graduationProbability.length - 1];
    }
    graduationProbability.push(gr150);

    if (graduationProbability[graduationProbability.length - 1] > gr200) {
      gr200 = graduationProbability[graduationProbability.length - 1];
    }
    graduationProbability.push(gr200);

    // safety hatch
    // let handJamProbability = 1.0;
    for (let i = graduationProbability.length - 1; i >= 0; i--) {
      // if (graduationProbability[i] == null)
      // {
      //   graduationProbability[i] = handJamProbability;
      // } else
      // {
      graduationProbability[i] = graduationProbability[i] / 100;
      // handJamProbability = graduationProbability[i];
      // }
    }
    return { graduationProbability, graduationTimeFactor };
  }

  /**
   * Recalculate refine education cost pell values if EFC has been updated (noted by grantsAndScholarshipsSummary.federalPellGrant == null).
   */
  private static recalculatePellGrantAndTotalValueOnRefineEducationCost(
    rocModel: RocModel,
    userModel: UserModel
  ): GrantsAndScholarshipsSummary {
    const grantsAndScholarshipsSummary: GrantsAndScholarshipsSummary = rocModel.refineEducationCost
      ?.grantsAndScholarshipsSummary ?? {
      federalPellGrant: [],
      otherFederalGrants: [],
      stateOrLocalGrants: [],
      institutionalGrants: [],
      otherGrants: [],
      giBillBenefits: [],
      dodTuitionAssistance: [],
      totalByYear: []
    };

    const pellGrantAidByYear = this.getPellGrantAidByYear(rocModel, userModel);
    grantsAndScholarshipsSummary.federalPellGrant = pellGrantAidByYear;

    for (let yearIndex = 0; yearIndex < (rocModel.yearsToCompleteDegree ?? 0); yearIndex++) {
      const totalForYear =
        (pellGrantAidByYear[yearIndex] ?? 0) +
        (grantsAndScholarshipsSummary.otherFederalGrants[yearIndex] ?? 0) +
        (grantsAndScholarshipsSummary.stateOrLocalGrants[yearIndex] ?? 0) +
        (grantsAndScholarshipsSummary.institutionalGrants[yearIndex] ?? 0) +
        (grantsAndScholarshipsSummary.otherGrants[yearIndex] ?? 0) +
        (grantsAndScholarshipsSummary.giBillBenefits[yearIndex] ?? 0) +
        (grantsAndScholarshipsSummary.dodTuitionAssistance[yearIndex] ?? 0);

      grantsAndScholarshipsSummary.totalByYear.push(totalForYear);
    }
    return grantsAndScholarshipsSummary;
  }

  /**
   * Calculate grantsByYear for part-time students. Normally zero for all values. If expectedFamilyContribution exists, manually calculate the pell grant aid per year.
   */
  private static getGrantsAndScholarshipsByYearPartTime(
    rocModel: RocModel,
    userModel: UserModel
  ): GrantsAndScholarshipsSummary {
    const grantsAndScholarshipsSummary: GrantsAndScholarshipsSummary = {
      federalPellGrant: [],
      otherFederalGrants: [],
      stateOrLocalGrants: [],
      institutionalGrants: [],
      otherGrants: [],
      giBillBenefits: [],
      dodTuitionAssistance: [],
      totalByYear: []
    };

    let pellGrantAidByYear!: number[];
    if (userModel.expectedFamilyContribution !== null) {
      pellGrantAidByYear = this.getPellGrantAidByYear(rocModel, userModel);
    }

    for (let yearIndex = 0; yearIndex < (rocModel.yearsToCompleteDegree ?? 0); yearIndex++) {
      const federalPellGrantForYear: number = pellGrantAidByYear?.[yearIndex] ?? 0;
      const otherFederalGrantsForYear: number = 0;
      const stateOrLocalGrantsForYear: number = 0;
      const institutionalGrantsForYear: number = 0;

      const otherGrantsForYear = 0;
      const giBillBenefitsForYear = 0;
      const dodTuitionAssistanceForYear = 0;
      const totalForYear =
        federalPellGrantForYear +
        otherFederalGrantsForYear +
        stateOrLocalGrantsForYear +
        institutionalGrantsForYear +
        otherGrantsForYear +
        giBillBenefitsForYear +
        dodTuitionAssistanceForYear;

      grantsAndScholarshipsSummary.federalPellGrant.push(federalPellGrantForYear);
      grantsAndScholarshipsSummary.otherFederalGrants.push(otherFederalGrantsForYear);
      grantsAndScholarshipsSummary.stateOrLocalGrants.push(stateOrLocalGrantsForYear);
      grantsAndScholarshipsSummary.institutionalGrants.push(institutionalGrantsForYear);
      grantsAndScholarshipsSummary.otherGrants.push(otherGrantsForYear);
      grantsAndScholarshipsSummary.giBillBenefits.push(giBillBenefitsForYear);
      grantsAndScholarshipsSummary.dodTuitionAssistance.push(dodTuitionAssistanceForYear);
      grantsAndScholarshipsSummary.totalByYear.push(totalForYear);
    }

    return grantsAndScholarshipsSummary;
  }

  /**
   * Calculate grantsByYear by manually calculating the pell grant aid per year and adding those values to the default IPEDS avgGrantScholarshipAid components.
   * The IPEDS avgGrantScholarshipAid components are increased by a default percent each year.
   */
  private static getGrantsAndScholarshipsByYearStrategy1(
    rocModel: RocModel,
    userModel: UserModel
  ): GrantsAndScholarshipsSummary {
    const aidBreakdownInfo = rocModel.institutionModel?.avgGrantScholarshipAidBreakdownInfo;
    const startingYearDelay = this.getStartingYearDelay(rocModel, userModel);

    const grantsAndScholarshipsSummary: GrantsAndScholarshipsSummary = {
      federalPellGrant: [],
      otherFederalGrants: [],
      stateOrLocalGrants: [],
      institutionalGrants: [],
      otherGrants: [],
      giBillBenefits: [],
      dodTuitionAssistance: [],
      totalByYear: []
    };

    const pellGrantAidByYear = this.getPellGrantAidByYear(rocModel, userModel);

    for (let yearIndex = 0; yearIndex < (rocModel.yearsToCompleteDegree ?? 0); yearIndex++) {
      const costOfLivingAdjustmentForYear = Math.pow(
        1 + CONFIG.COST_OF_LIVING_ADJUSTMENT,
        yearIndex + startingYearDelay.total
      );
      const otherFederalGrantsForYear = Math.round(
        (aidBreakdownInfo?.federalGrants.otherFederalGrants.avgAmountAidReceived ?? 0) * costOfLivingAdjustmentForYear
      );
      const stateOrLocalGrantsForYear = Math.round(
        (aidBreakdownInfo?.stateLocalGovtGrantOrScholarships.avgAmountAidReceived ?? 0) * costOfLivingAdjustmentForYear
      );
      const institutionalGrantsForYear = Math.round(
        (aidBreakdownInfo?.institutionalGrantsOrScholarships.avgAmountAidReceived ?? 0) * costOfLivingAdjustmentForYear
      );

      const otherGrantsForYear = 0;
      const giBillBenefitsForYear = 0;
      const dodTuitionAssistanceForYear = 0;
      const totalForYear =
        (pellGrantAidByYear[yearIndex] ?? 0) +
        (otherFederalGrantsForYear ?? 0) +
        (stateOrLocalGrantsForYear ?? 0) +
        (institutionalGrantsForYear ?? 0) +
        (otherGrantsForYear ?? 0) +
        (giBillBenefitsForYear ?? 0) +
        (dodTuitionAssistanceForYear ?? 0);

      grantsAndScholarshipsSummary.federalPellGrant.push(pellGrantAidByYear[yearIndex]);
      grantsAndScholarshipsSummary.otherFederalGrants.push(otherFederalGrantsForYear);
      grantsAndScholarshipsSummary.stateOrLocalGrants.push(stateOrLocalGrantsForYear);
      grantsAndScholarshipsSummary.institutionalGrants.push(institutionalGrantsForYear);
      grantsAndScholarshipsSummary.otherGrants.push(otherGrantsForYear);
      grantsAndScholarshipsSummary.giBillBenefits.push(giBillBenefitsForYear);
      grantsAndScholarshipsSummary.dodTuitionAssistance.push(dodTuitionAssistanceForYear);
      grantsAndScholarshipsSummary.totalByYear.push(totalForYear);
    }

    return grantsAndScholarshipsSummary;
  }

  /**
   * Calculate grantsByYear by using default IPEDS avgGrantScholarshipAid value for a given income range. A default percent increase is applied each year.
   */
  private static getGrantsAndScholarshipsByYearStrategy2(
    rocModel: RocModel,
    userModel: UserModel
  ): GrantsAndScholarshipsSummary {
    let avgAmountAidReceived = 0;
    const startingYearDelay = this.getStartingYearDelay(rocModel, userModel);

    const grantsAndScholarshipsSummary: GrantsAndScholarshipsSummary = {
      federalPellGrant: [],
      otherFederalGrants: [],
      stateOrLocalGrants: [],
      institutionalGrants: [],
      otherGrants: [],
      giBillBenefits: [],
      dodTuitionAssistance: [],
      totalByYear: []
    };

    if (rocModel.institutionModel) {
      switch (userModel.incomeRange.value) {
        case IncomeRangeEnum.From_0_To_30000.value:
          avgAmountAidReceived = rocModel.institutionModel.avgGrantScholarshipAidByIncomeInfo.income0To30000;
          break;
        case IncomeRangeEnum.From_30001_To_48000.value:
          avgAmountAidReceived = rocModel.institutionModel.avgGrantScholarshipAidByIncomeInfo.income30001To48000;
          break;
        case IncomeRangeEnum.From_48001_To_75000.value:
          avgAmountAidReceived = rocModel.institutionModel.avgGrantScholarshipAidByIncomeInfo.income48001To75000;
          break;
        case IncomeRangeEnum.From_75001_To_110000.value:
          avgAmountAidReceived = rocModel.institutionModel.avgGrantScholarshipAidByIncomeInfo.income75001To110000;
          break;
        case IncomeRangeEnum.From_110001_Or_More.value:
          avgAmountAidReceived = rocModel.institutionModel.avgGrantScholarshipAidByIncomeInfo.incomeOver110000;
          break;
      }
    }

    for (let yearIndex = 0; yearIndex < (rocModel.yearsToCompleteDegree ?? 0); yearIndex++) {
      const federalPellGrantForYear: number = 0;
      const otherFederalGrantsForYear: number = 0;
      const stateOrLocalGrantsForYear: number = 0;
      const institutionalGrantsForYear: number = 0;

      const otherGrantsForYear = 0;
      const giBillBenefitsForYear = 0;
      const dodTuitionAssistanceForYear = 0;
      const totalForYear = Math.round(
        avgAmountAidReceived * Math.pow(1 + CONFIG.COST_OF_LIVING_ADJUSTMENT, yearIndex + startingYearDelay.total)
      );

      grantsAndScholarshipsSummary.federalPellGrant.push(federalPellGrantForYear);
      grantsAndScholarshipsSummary.otherFederalGrants.push(otherFederalGrantsForYear);
      grantsAndScholarshipsSummary.stateOrLocalGrants.push(stateOrLocalGrantsForYear);
      grantsAndScholarshipsSummary.institutionalGrants.push(institutionalGrantsForYear);
      grantsAndScholarshipsSummary.otherGrants.push(otherGrantsForYear);
      grantsAndScholarshipsSummary.giBillBenefits.push(giBillBenefitsForYear);
      grantsAndScholarshipsSummary.dodTuitionAssistance.push(dodTuitionAssistanceForYear);
      grantsAndScholarshipsSummary.totalByYear.push(totalForYear);
    }

    return grantsAndScholarshipsSummary;
  }

  /**
   * Calculate grantsByYear by using default IPEDS avgGrantScholarshipAid value. A default percent increase is applied each year.
   */
  private static getGrantsAndScholarshipsByYearStrategy3(
    rocModel: RocModel,
    userModel: UserModel
  ): GrantsAndScholarshipsSummary {
    const aidBreakdownInfo = rocModel.institutionModel?.avgGrantScholarshipAidBreakdownInfo;
    const startingYearDelay = this.getStartingYearDelay(rocModel, userModel);

    const grantsAndScholarshipsSummary: GrantsAndScholarshipsSummary = {
      federalPellGrant: [],
      otherFederalGrants: [],
      stateOrLocalGrants: [],
      institutionalGrants: [],
      otherGrants: [],
      giBillBenefits: [],
      dodTuitionAssistance: [],
      totalByYear: []
    };

    for (let yearIndex = 0; yearIndex < (rocModel.yearsToCompleteDegree ?? 0); yearIndex++) {
      const costOfLivingAdjustmentForYear = Math.pow(
        1 + CONFIG.COST_OF_LIVING_ADJUSTMENT,
        yearIndex + startingYearDelay.total
      );

      const federalPellGrantForYear = Math.round(
        (aidBreakdownInfo?.federalGrants.pellGrants.avgAmountAidReceived ?? 0) * costOfLivingAdjustmentForYear
      );
      const otherFederalGrantsForYear = Math.round(
        (aidBreakdownInfo?.federalGrants.otherFederalGrants.avgAmountAidReceived ?? 0) * costOfLivingAdjustmentForYear
      );
      const stateOrLocalGrantsForYear = Math.round(
        (aidBreakdownInfo?.stateLocalGovtGrantOrScholarships.avgAmountAidReceived ?? 0) * costOfLivingAdjustmentForYear
      );
      const institutionalGrantsForYear = Math.round(
        (aidBreakdownInfo?.institutionalGrantsOrScholarships.avgAmountAidReceived ?? 0) * costOfLivingAdjustmentForYear
      );

      const otherGrantsForYear = 0;
      const giBillBenefitsForYear = 0;
      const dodTuitionAssistanceForYear = 0;
      const totalForYear =
        (federalPellGrantForYear ?? 0) +
        (otherFederalGrantsForYear ?? 0) +
        (stateOrLocalGrantsForYear ?? 0) +
        (institutionalGrantsForYear ?? 0) +
        (otherGrantsForYear ?? 0) +
        (giBillBenefitsForYear ?? 0) +
        (dodTuitionAssistanceForYear ?? 0);

      grantsAndScholarshipsSummary.federalPellGrant.push(federalPellGrantForYear);
      grantsAndScholarshipsSummary.otherFederalGrants.push(otherFederalGrantsForYear);
      grantsAndScholarshipsSummary.stateOrLocalGrants.push(stateOrLocalGrantsForYear);
      grantsAndScholarshipsSummary.institutionalGrants.push(institutionalGrantsForYear);
      grantsAndScholarshipsSummary.otherGrants.push(otherGrantsForYear);
      grantsAndScholarshipsSummary.giBillBenefits.push(giBillBenefitsForYear);
      grantsAndScholarshipsSummary.dodTuitionAssistance.push(dodTuitionAssistanceForYear);
      grantsAndScholarshipsSummary.totalByYear.push(totalForYear);
    }

    return grantsAndScholarshipsSummary;
  }

  private static getLoanLimitsForYear = (
    isTaxDependent: boolean,
    yearOfCollege: number,
    costOfAttendanceForYear: number,
    efc: number | null
  ): {
    subsidizedLoanLimitForYear: number;
    totalFederalLoanLimitForYear: number;
    isEligibleForSubsidizedLoanForYear: boolean;
  } => {
    let subsidizedLoanLimitForYear: number;
    let totalFederalLoanLimitForYear: number;
    let isEligibleForSubsidizedLoanForYear = false;

    if (isTaxDependent) {
      if (yearOfCollege === 1) {
        subsidizedLoanLimitForYear = CONFIG.LOAN_CONSTANTS.DEPENDENT.FEDERAL_SUBSIDIZED.YEAR_1;
        totalFederalLoanLimitForYear = CONFIG.LOAN_CONSTANTS.DEPENDENT.TOTAL_FEDERAL_LOAN.YEAR_1;
      } else if (yearOfCollege === 2) {
        subsidizedLoanLimitForYear = CONFIG.LOAN_CONSTANTS.DEPENDENT.FEDERAL_SUBSIDIZED.YEAR_2;
        totalFederalLoanLimitForYear = CONFIG.LOAN_CONSTANTS.DEPENDENT.TOTAL_FEDERAL_LOAN.YEAR_2;
      } else {
        subsidizedLoanLimitForYear = CONFIG.LOAN_CONSTANTS.DEPENDENT.FEDERAL_SUBSIDIZED.YEAR_3_PLUS;
        totalFederalLoanLimitForYear = CONFIG.LOAN_CONSTANTS.DEPENDENT.TOTAL_FEDERAL_LOAN.YEAR_3_PLUS;
      }
    } else {
      if (yearOfCollege === 1) {
        subsidizedLoanLimitForYear = CONFIG.LOAN_CONSTANTS.INDEPENDENT.FEDERAL_SUBSIDIZED.YEAR_1;
        totalFederalLoanLimitForYear = CONFIG.LOAN_CONSTANTS.INDEPENDENT.TOTAL_FEDERAL_LOAN.YEAR_1;
      } else if (yearOfCollege === 2) {
        subsidizedLoanLimitForYear = CONFIG.LOAN_CONSTANTS.INDEPENDENT.FEDERAL_SUBSIDIZED.YEAR_2;
        totalFederalLoanLimitForYear = CONFIG.LOAN_CONSTANTS.INDEPENDENT.TOTAL_FEDERAL_LOAN.YEAR_2;
      } else {
        subsidizedLoanLimitForYear = CONFIG.LOAN_CONSTANTS.INDEPENDENT.FEDERAL_SUBSIDIZED.YEAR_3_PLUS;
        totalFederalLoanLimitForYear = CONFIG.LOAN_CONSTANTS.INDEPENDENT.TOTAL_FEDERAL_LOAN.YEAR_3_PLUS;
      }
    }

    // if efc exists and coa - efc > 0, subsidized loan is either minimum of coa - efc or default max sub loan from config file,
    // if coa - efc <= 0 , no sub loan but can get unsubsidized loan up to total_federal_loan
    if (efc != null && efc != undefined) {
      const unmetNeed = Math.round(costOfAttendanceForYear - efc);

      if (unmetNeed > 0) {
        isEligibleForSubsidizedLoanForYear = true;
        if (subsidizedLoanLimitForYear > unmetNeed) {
          subsidizedLoanLimitForYear = unmetNeed;
        }
      } else if (unmetNeed <= 0) {
        subsidizedLoanLimitForYear = 0;
      }
    }

    const result = {
      subsidizedLoanLimitForYear,
      totalFederalLoanLimitForYear,
      isEligibleForSubsidizedLoanForYear
    };

    return result;
  };

  private static getPellGrantAidForYear = (
    adjustedEfc: number,
    costOfAttendance: number,
    fullTimeStudentPercent: number,
    pellGrantMaxLimit: number,
    pellGrantMinLimitForYear: number
  ): number => {
    if (adjustedEfc === null) {
      adjustedEfc = 1e20;
    }

    let pellGrantAid = (costOfAttendance - adjustedEfc) * fullTimeStudentPercent;

    if (pellGrantAid < pellGrantMinLimitForYear) {
      pellGrantAid = 0;
    } else if (pellGrantAid > pellGrantMaxLimit) {
      pellGrantAid = pellGrantMaxLimit;
    }

    return pellGrantAid;
  };

  private static calculateTuitionAndFees(rocModel: RocModel): CostLineItem {
    // default to in-state if residencyType null or UNKNOWN
    const tuitionAndFeesModel = rocModel?.institutionModel?.costOfAttendanceInfo.tuitionAndFees;
    const residencyType = rocModel?.residencyType ?? ResidencyTypeEnum.IN_STATE;
    const isFullTime = rocModel?.isFulltime;

    let tuitionAndFees = 0;
    let tuitionAndFeesRaise = 0;

    if (tuitionAndFeesModel) {
      switch (residencyType.value) {
        case ResidencyTypeEnum.UNKNOWN.value:
        case ResidencyTypeEnum.IN_STATE.value:
          tuitionAndFees =
            (isFullTime ? tuitionAndFeesModel.inState.fullTimeExpense : tuitionAndFeesModel.inState.partTimeExpense) ??
            0;
          tuitionAndFeesRaise = tuitionAndFeesModel.inState.percentChangeFromLastYear ?? 0;
          break;
        case ResidencyTypeEnum.IN_DISTRICT.value:
          tuitionAndFees =
            (isFullTime
              ? tuitionAndFeesModel.inDistrict.fullTimeExpense
              : tuitionAndFeesModel.inDistrict.partTimeExpense) ?? 0;
          tuitionAndFeesRaise = tuitionAndFeesModel.inDistrict.percentChangeFromLastYear ?? 0;
          break;
        case ResidencyTypeEnum.OUT_STATE.value:
          tuitionAndFees =
            (isFullTime
              ? tuitionAndFeesModel.outOfState.fullTimeExpense
              : tuitionAndFeesModel.outOfState.partTimeExpense) ?? 0;
          tuitionAndFeesRaise = tuitionAndFeesModel.outOfState.percentChangeFromLastYear ?? 0;
          break;
        default:
          tuitionAndFees =
            (isFullTime ? tuitionAndFeesModel.inState.fullTimeExpense : tuitionAndFeesModel.inState.partTimeExpense) ??
            0;
          tuitionAndFeesRaise = tuitionAndFeesModel.inState.percentChangeFromLastYear ?? 0;
          break;
      }
    }

    const costLineItem: CostLineItem = {
      expenseAmount: tuitionAndFees,
      percentChangeFromLastYear: tuitionAndFeesRaise / 100
    };

    return costLineItem;
  }

  private static calculateBooksAndSupplies(rocModel: RocModel): CostLineItem {
    const costOfAttendanceBreakdownModel: CostOfAttendanceStandardBreakdown | undefined =
      rocModel?.institutionModel?.costOfAttendanceInfo.booksAndSupplies;

    const costLineItem: CostLineItem = {
      expenseAmount: costOfAttendanceBreakdownModel?.expenseAmount ?? null,
      percentChangeFromLastYear: (costOfAttendanceBreakdownModel?.percentChangeFromLastYear ?? 0) / 100
    };

    return costLineItem;
  }

  private static calculateRoomAndBoard(rocModel: RocModel): CostLineItem {
    // default to on campus if livingConditionType null or UNKNOWN
    const livingArrangementCostInfo: LivingArrangement | undefined =
      rocModel?.institutionModel?.costOfAttendanceInfo.livingArrangement;
    const livingConditionType: LivingConditionTypeEnum =
      rocModel?.livingConditionTypeEnum ?? LivingConditionTypeEnum.ON_CAMPUS;

    let roomAndBoardCost = 0;
    let roomAndBoardCostRaise = 0;

    if (livingArrangementCostInfo) {
      switch (livingConditionType.value) {
        case LivingConditionTypeEnum.UNKNOWN.value:
        case LivingConditionTypeEnum.ON_CAMPUS.value:
          roomAndBoardCost = livingArrangementCostInfo.onCampus.roomAndBoard.expenseAmount ?? 0;
          roomAndBoardCostRaise = livingArrangementCostInfo.onCampus.roomAndBoard.percentChangeFromLastYear ?? 0;
          break;
        case LivingConditionTypeEnum.OFF_CAMPUS_NOT_WITH_FAMILY.value:
          roomAndBoardCost = livingArrangementCostInfo.offCampusNotWithFamily.roomAndBoard.expenseAmount ?? 0;
          roomAndBoardCostRaise =
            livingArrangementCostInfo.offCampusNotWithFamily.roomAndBoard.percentChangeFromLastYear ?? 0;
          break;
        case LivingConditionTypeEnum.OFF_CAMPUS_WITH_FAMILY.value:
          roomAndBoardCost = 0;
          roomAndBoardCostRaise = 0;
          break;
        default:
          roomAndBoardCost = livingArrangementCostInfo.onCampus.roomAndBoard.expenseAmount ?? 0;
          roomAndBoardCostRaise = livingArrangementCostInfo.onCampus.roomAndBoard.percentChangeFromLastYear ?? 0;
          break;
      }
    }

    const costLineItem: CostLineItem = {
      expenseAmount: roomAndBoardCost,
      percentChangeFromLastYear: roomAndBoardCostRaise / 100
    };

    return costLineItem;
  }

  private static calculateOtherLivingExpenses(rocModel: RocModel): CostLineItem {
    // default to on campus if livingConditionType null or UNKNOWN
    const livingArrangementCostInfo: LivingArrangement | undefined =
      rocModel?.institutionModel?.costOfAttendanceInfo.livingArrangement;
    const livingConditionType: LivingConditionTypeEnum =
      rocModel?.livingConditionTypeEnum ?? LivingConditionTypeEnum.ON_CAMPUS;

    let otherLivingExpenses = 0;
    let otherLivingExpensesRaise = 0;

    if (livingArrangementCostInfo) {
      switch (livingConditionType.value) {
        case LivingConditionTypeEnum.UNKNOWN.value:
        case LivingConditionTypeEnum.ON_CAMPUS.value:
          otherLivingExpenses = livingArrangementCostInfo.onCampus.otherExpenses.expenseAmount ?? 0;
          otherLivingExpensesRaise = livingArrangementCostInfo.onCampus.otherExpenses.percentChangeFromLastYear ?? 0;
          break;
        case LivingConditionTypeEnum.OFF_CAMPUS_NOT_WITH_FAMILY.value:
          otherLivingExpenses = livingArrangementCostInfo.offCampusNotWithFamily.otherExpenses.expenseAmount ?? 0;
          otherLivingExpensesRaise =
            livingArrangementCostInfo.offCampusNotWithFamily.otherExpenses.percentChangeFromLastYear ?? 0;
          break;
        case LivingConditionTypeEnum.OFF_CAMPUS_WITH_FAMILY.value:
          otherLivingExpenses = livingArrangementCostInfo.offCampusWithFamily.otherExpenses.expenseAmount ?? 0;
          otherLivingExpensesRaise =
            livingArrangementCostInfo.offCampusWithFamily.otherExpenses.percentChangeFromLastYear ?? 0;
          break;
        default:
          otherLivingExpenses = livingArrangementCostInfo.onCampus.otherExpenses.expenseAmount ?? 0;
          otherLivingExpensesRaise = livingArrangementCostInfo.onCampus.otherExpenses.percentChangeFromLastYear ?? 0;
          break;
      }
    }

    const costLineItem: CostLineItem = {
      expenseAmount: otherLivingExpenses,
      percentChangeFromLastYear: otherLivingExpensesRaise / 100
    };

    return costLineItem;
  }
}
